/**
 * 🧭 Konto-Profil — Einstellungen, die für ALLE Geräte eines Kontos gelten.
 *
 * Bisher lag jede Einstellung nur auf dem Gerät (localStorage); jedes neue Handy musste von Hand
 * eingerichtet werden. Mit dem Setup wird ein Profil am KONTO gespeichert (users.app_profile):
 *  - ein neues Gerät übernimmt es beim Anmelden,
 *  - ändert jemand an einem Gerät eine dieser Einstellungen, gilt das fürs ganze Konto.
 *
 * Konten OHNE Profil (alle bestehenden, bis jemand das Setup durchläuft) verhalten sich exakt
 * wie vorher: nichts wird übernommen, nichts hochgeladen.
 *
 * Am Gerät bleiben bewusst: Druckerkopplung (BT-/Netzwerk-Adresse), Geräterolle,
 * Kamera-Feinabstimmung, Mitarbeiter-Modus an/aus.
 */
import { saveAccountProfile } from './serverDataService';

// Setup, Konto-Profil, neuer Drucker-Dialog und die neuen Anleitungen sind seit 01.10.2026 für alle an.
// Not-Aus pro Gerät: localStorage kiosk_setup_preview = '0' → alles wie vor dem Update (alte Sprachzeile,
// alter Drucker-Dialog, kein Abgleich). ('1' schaltet zusätzlich den Schein-Drucker für Tests frei.)
const PREVIEW_KEY = 'kiosk_setup_preview';
export function isSetupEnabled() {
  try { return localStorage.getItem(PREVIEW_KEY) !== '0'; } catch { return true; }
}

// Geräte-Einstellungen, die ins Konto-Profil gehören (muss zu server/lib/appProfile.js passen).
export const PROFILE_SETTINGS = {
  app_language: /^[a-z]{2}$/,
  // Druckertyp, Etikettgröße und Ränder gehören ebenfalls ins Konto-Profil (25.10.2026: alle Handys eines Shops
  // drucken auf denselben Etiketten/Druckern; nur die BT-/Netzwerk-Kopplungsadresse bleibt am einzelnen Gerät).
  kiosk_label_lang: /^(cpcl|zpl)$/,
  kiosk_label_size: /^(endlos|(?:[3-9]\d|10\d|110)x(?:0|[2-9]\d|[12]\d\d|300))$/,
  kiosk_label_rand: /^(?:-[1-8]|[12]?\d|30),(?:[12]?\d|30),(?:\d|1[0-5])$/,
  kiosk_label_layout: /^(hausnummer|nachname|eigen|grenzpaket)$/,
  kiosk_label_rahmen: /^[01]$/,
  kiosk_label_kopf: /^(hausnummer|nachname|name|initialen|fach)$/,
  kiosk_label_zeilen: /^(keine|(fach|name|initialen|strasse|absender|dienst|datum)(,(fach|name|initialen|strasse|absender|dienst|datum)){0,6})$/, // Reihenfolge = Druck-Reihenfolge
  kiosk_label_dienst: /^[01]$/,
  kiosk_label_code: /^(keine|(barcode|qr|beide):(klein|mittel|gross))$/, // Barcode/QR der Sendungsnummer unten aufs Etikett
  kiosk_label_gruss: /^[\x20-\x7e\u00a0-\u024f]{0,60}$/,                 // Eigener Grußtext unter dem Kasten
  kiosk_label_grafik: /^(keine|preset:(halloween|weihnachten|festtage|herz)|custom:\d{1,2}:\d{1,3}:[0-9A-F]{32,38400})$/, // 1-Bit-Grafik (^GFA)
  kiosk_label_autoprint: /^[01]$/,
  kiosk_regal_auto_letter: /^[01]$/,
  // Unterfächer je Buchstabe: 'B:f,k,p,u;S:j' (Anfangsbuchstaben der Unterfächer 2…n), 'keine' = ohne
  kiosk_regal_sub: /^(keine|[A-Z]:[b-z](,[b-z]){0,19}(;[A-Z]:[b-z](,[b-z]){0,19}){0,25})$/,
  kiosk_inapp_camera: /^[01]$/,
  kiosk_series_mode: /^[01]$/,
  kiosk_qr_regal_scan: /^[01]$/,
  kiosk_capture_sender: /^[01]$/, // Absender (falls auf dem Etikett) am Paket speichern
  kiosk_list_carrier: /^[01]$/, // Paketdienst in der Paketliste anzeigen
  kiosk_vinted_sort: /^[01]$/,    // Vinted-Pakete nach Kurznummer einsortieren (Fach V0–V9, Nummer groß aufs Etikett)
  kiosk_carrier_sort: /^(keine|[a-z0-9_-]{2,24}:(hausnummer|nachname|kurznummer)(;[a-z0-9_-]{2,24}:(hausnummer|nachname|kurznummer)){0,19})$/, // Pro Paketdienst abweichende Sortierung (z. B. 'postnl:hausnummer;vinted:kurznummer')
  kiosk_branche: /^(paketshop|pakketpunt_nl|buero|wohnanlage|gepaeck|click_collect)$/, // Einsatzbereich / Vorlage („Was bist du?")
  kiosk_addressbook_enabled: /^[01]$/, // Empfänger-/Stammkunden-Liste (CSV & Kontakte) sichtbar/aktiv (Standard 0 bei Paketshop)
  kiosk_list_notify_mode: /^(staff|ask|auto)$/, // Bei Listen-Treffer: 'staff' = nur Mitarbeiter-Hinweis, 'ask' = fragen ob E-Mail, 'auto' = Auto-E-Mail
  kiosk_auto_notify: /^[01]$/,       // Empfänger aus Adressbuch/Warteliste beim Speichern automatisch per E-Mail benachrichtigen
  kiosk_require_signature: /^[01]$/, // Unterschrift bei Paket-Ausgabe auf dem Bildschirm verlangen
  kiosk_tablet_mode: /^[01]$/,       // Tablet-Suchmodus (Kunden-Suchterminal / Live-Suchtreffer)
  kiosk_auto_number: /^[01]$/,       // Automatische Laufnummer (#001, #002…), falls kein Name auf dem Gegenstand steht
  kiosk_id_first: /^[01]$/,          // „Ausweis zuerst fotografieren" beim Erfassen (Gepäck / Fundbüro / Verleih)
  kiosk_double_label: /^[01]$/,      // 2. Etikett als Kunden-Abholbon drucken
  kiosk_privacy_consent: /^[01]$/,   // Eigene Datenschutz-/AGB-Bestätigung vom Kunden vor dem Speichern verlangen
  kiosk_privacy_text: /^[\s\S]{0,600}$/, // Individueller Datenschutz-/AGB-Text (max. 600 Zeichen)
  kiosk_delete_days: /^\d{1,5}$/, // Speicherfrist nach Ausgabe in Tagen (Standard: 5; 0 = sofort/1 Std.; max. 1825 = 5 Jahre)
  kiosk_ignore_recipient: /^[\x20-\x7e\u00a0-\u024f]{0,200}$/, // Eigener Shop-Name / gesperrte Empfänger-Begriffe (kommagetrennt)
  kiosk_vinted_bridge_on: /^[01]$/,    // Vinted-Anzeige eingeschaltet
  kiosk_vinted_live_annahme: /^[01]$/, // Vinted-Annahme live buchen
  kiosk_vinted_live_ausgabe: /^[01]$/, // Vinted-Ausgabe live buchen
  kiosk_turbo: /^[01]$/,               // Turbo Vinted/DHL eingeschaltet
  kiosk_dark_mode: /^[01]$/,           // Dunkelmodus (für alle Geräte des Kontos)
  kiosk_show_details: /^[01]$/,        // Filter „Details anzeigen" in der Paketliste
  kiosk_show_images: /^[01]$/,         // Filter „Fotos anzeigen" in der Paketliste
  kiosk_show_regale: /^[01]$/,         // Filter „Regale anzeigen" in der Paketliste
  kiosk_show_filters: /^[01]$/,        // Filter-Zeile ein-/ausgeklappt
  kiosk_show_sender: /^[01]$/,         // Filter „Absender anzeigen" in der Paketliste
  kiosk_capture_palette: /^[01]$/,     // Paletten-/Gebinde-Auswahl beim Erfassen anzeigen
  kiosk_bevollmaechtigter: /^[01]$/,   // Ausgabe an Bevollmächtigten (mit Name, Vorname & Vollmacht-Foto) ermöglichen (Standard 0)
  kiosk_dhl_stick_ip: /^[\x20-\x7e]{0,120}$/, // Standard-IP des DHL USB-Sticks im WLAN
  kiosk_dhl_photo_schnell: /^[01]$/,   // Foto → sofort Schnelleinlagern (nur wiesn2022@)
  kiosk_dhl_stick_queue_mode: /^(review|auto)$/, // Warteschlangen-Modus des DHL-Sticks (nur wiesn2022@)
  kiosk_stamps_state: /^[\s\S]{0,4000}$/, // Briefmarken-10er-Zählerstand (JSON, nur wiesn2022@)
  formSectionVisibility: /^\{"[a-zA-Z]+":(true|false)(,"[a-zA-Z]+":(true|false)){0,15}\}$/, // Ein-/ausgeklappte Sektionen im Erfassungs-Modal
};

// Frei einschaltbare Premium-Funktionen (früher nur je Konto von uns freigeschaltet).
export const PROFILE_FEATURES = ['nachnameSortierung', 'vintedImport', 'staffMode', 'batchBarcodes', 'seriesMode', 'palettenvertrieb'];

const CACHE_KEY = 'kiosk_profile'; // zuletzt bekanntes Profil (fürs Setup als Vorbelegung)

function lesen(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

/** Aktuelle Werte dieses Geräts für alle Profil-Einstellungen (nur gültige, gesetzte Werte). */
export function collectLocalSettings() {
  const out = {};
  for (const [key, re] of Object.entries(PROFILE_SETTINGS)) {
    const v = lesen(key);
    if (typeof v === 'string' && re.test(v)) {
      if (key === 'kiosk_delete_days') {
        const n = parseInt(v, 10);
        out[key] = String(Number.isFinite(n) ? Math.min(1825, Math.max(0, n)) : 5);
      } else {
        out[key] = v;
      }
    }
  }
  return out;
}

function gekoppelterDruckerTyp() {
  try {
    const raw = lesen('kiosk_label_printer');
    const name = raw ? JSON.parse(raw)?.name : '';
    if (!name) return null;
    if (/\b(PC4[2-5]|PD4[35]|PM4[2-5]|PX[46]|Honeywell|Intermec|Zebra|Z[DTQ]\d|G[KX]4\d|TSC|TE2\d0|TTP-|DA2\d0)/i.test(name)) return 'zpl';
    if (/bixolon|SPP-|SRP-|XM7/i.test(name)) return 'cpcl';
    return null;
  } catch { return null; }
}

/**
 * Profil-Einstellungen auf dieses Gerät schreiben.
 * Ist an diesem Gerät ein Drucker gekoppelt, dessen Name eindeutig ZPL oder CPCL verrät,
 * hat dieser Vorrang vor einem abweichenden Profilwert (verhindert stummen CPCL-Druck auf ZPL-Tischdruckern).
 * @returns {string[]} Schlüssel, deren Wert sich dadurch geändert hat
 */
export function applySettings(settings) {
  const changed = [];
  const festerTyp = gekoppelterDruckerTyp();
  for (const [key, re] of Object.entries(PROFILE_SETTINGS)) {
    let v = settings ? settings[key] : undefined;
    if (typeof v !== 'string' || !re.test(v)) continue;
    if (key === 'kiosk_label_lang' && festerTyp) v = festerTyp;
    if (lesen(key) === v) continue;
    try { localStorage.setItem(key, v); changed.push(key); } catch { /* */ }
  }
  return changed;
}

/** Welche Einstellungen weichen lokal vom letzten Abgleich ab? */
export function diffSettings(snapshot, current) {
  const out = {};
  for (const key of Object.keys(PROFILE_SETTINGS)) {
    if (current[key] !== undefined && current[key] !== (snapshot || {})[key]) out[key] = current[key];
  }
  return out;
}

export function getCachedProfile() {
  try { const p = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null'); return p && p.v >= 1 ? p : null; } catch { return null; }
}

/** 🔑 Profil-Code im gemerkten Profil nachführen (nach „Code erzeugen" / „Teilen beenden" im Setup) */
export function setCachedShareCode(code) {
  const p = getCachedProfile();
  if (!p) return;
  if (code) p.shareCode = code; else delete p.shareCode;
  cacheProfile(p);
}

function cacheProfile(profile) {
  try {
    if (profile) localStorage.setItem(CACHE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(CACHE_KEY);
  } catch { /* */ }
}

// ── Abgleich ────────────────────────────────────────────────────────────────
let snapshot = null;   // Stand der Profil-Einstellungen beim letzten Abgleich (null = kein Profil → kein Abgleich)
let timer = null;
let sendet = false;
let pausiert = false;  // Während eines laufenden Drucker-Testdrucks keine Zwischenstände ans Konto melden
let uebergangGeprueft = false;

export function setProfileSyncPaused(on) {
  pausiert = !!on;
}

function benachrichtige(changed) {
  try {
    window.dispatchEvent(new CustomEvent('kiosk:profile-applied', { detail: { changed } }));
    if (changed.some((k) => k.startsWith('kiosk_vinted_'))) {
      window.dispatchEvent(new CustomEvent('kiosk:custom-features'));
    }
  } catch { /* */ }
}

/** Lokale Änderungen ans Konto melden (nur wenn das Konto ein Profil hat). */
export async function pushLocalChanges() {
  if (!snapshot || sendet || pausiert || !isSetupEnabled()) return false;
  const jetzt = collectLocalSettings();
  const patch = diffSettings(snapshot, jetzt);
  if (!Object.keys(patch).length) return false;
  sendet = true;
  try {
    const res = await saveAccountProfile({ settings: patch });
    if (res && res.profile) { snapshot = { ...snapshot, ...patch }; cacheProfile(res.profile); }
    return true;
  } catch {
    return false; // offline o. ä. → beim nächsten Durchlauf erneut
  } finally {
    sendet = false;
  }
}

const NEU_IM_PROFIL = {
  kiosk_label_lang: 'cpcl',
  kiosk_label_size: '100x150',
  kiosk_label_rand: '0,0,0',
  kiosk_vinted_bridge_on: '0',
  kiosk_vinted_live_annahme: '0',
  kiosk_vinted_live_ausgabe: '0',
  kiosk_turbo: '0',
  kiosk_dark_mode: '0',
  kiosk_show_details: '0',
  kiosk_show_images: '0',
  kiosk_show_regale: '0',
  kiosk_show_filters: '1',
  kiosk_show_sender: '0',
  kiosk_capture_palette: '0',
  formSectionVisibility: '{"addressSection":true,"postalSection":false,"shelfSection":true,"trackingSection":true,"extrasSection":false,"dateSection":false,"favoritesList":false}',
};
let debounceTimer = null;

function starteBeobachter() {
  if (timer || typeof window === 'undefined') return;
  timer = setInterval(pushLocalChanges, 10000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') pushLocalChanges(); });
  if (typeof window.addEventListener === 'function') {
    window.addEventListener('kiosk:label-cfg-changed', () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => { debounceTimer = null; pushLocalChanges(); }, 600);
    });
  }
}

/**
 * Vom Server geladenes Profil verarbeiten (kommt mit /premium/status).
 * Ohne Profil oder ohne Vorschau-Schalter passiert nichts.
 */
export function handleServerProfile(profile) {
  if (!isSetupEnabled()) return [];
  if (!profile || !(profile.v >= 1)) { snapshot = null; cacheProfile(null); return []; }
  cacheProfile(profile);
  try {
    // Hat das Konto bereits ein gespeichertes Setup-Profil, ist das Setup auf diesem Konto erledigt:
    // Auf einem frischen Zweitgerät weder „Neu: Jetzt Setup starten"-Popup noch rote NEU-Plakette zeigen.
    if (profile.settings && Object.keys(profile.settings).length > 0) {
      localStorage.setItem('kiosk_setup_seen', '1');
      localStorage.setItem('kiosk_whatsnew_seen', '2026-10-setup');
      localStorage.setItem('kiosk_demo_hidden', '1');
    }
    if (profile.features?.batchBarcodes) {
      localStorage.removeItem('kiosk_batch_barcodes_disabled');
    }
  } catch { /* */ }
  // Ausstehende lokale Änderungen (seit dem letzten Snapshot geändert, aber noch nicht ans Konto gemeldet)
  // nicht durch den älteren Serverstand überschreiben:
  const lokalOffen = snapshot ? diffSettings(snapshot, collectLocalSettings()) : {};
  const serverSettings = Object.keys(lokalOffen).length
    ? Object.fromEntries(Object.entries(profile.settings || {}).filter(([k]) => !(k in lokalOffen)))
    : profile.settings;
  const changed = applySettings(serverSettings);
  snapshot = collectLocalSettings();
  for (const k of Object.keys(lokalOffen)) {
    if (profile.settings?.[k] === undefined) delete snapshot[k];
    else snapshot[k] = profile.settings[k];
  }
  // Einmalig je Sitzung: Hat ein bestehendes Konto im Server-Profil noch keine Etikettgröße/Ränder/Druckersprache,
  // aber dieses Gerät hat bereits vom Standard abweichende Werte, beim nächsten Durchlauf ans Konto melden.
  if (!uebergangGeprueft) {
    uebergangGeprueft = true;
    for (const [k, std] of Object.entries(NEU_IM_PROFIL)) {
      if (profile.settings?.[k] === undefined && snapshot[k] && snapshot[k] !== std) {
        delete snapshot[k];
      }
    }
  }
  starteBeobachter();
  benachrichtige(changed);
  return changed;
}

/**
 * Setup abgeschlossen: Einstellungen auf dieses Gerät schreiben und als Konto-Profil speichern
 * (ersetzt ein vorhandenes Profil).
 */
export async function saveSetup({ settings, features, carriers }) {
  try {
    if (features && features.batchBarcodes) {
      localStorage.removeItem('kiosk_batch_barcodes_disabled');
    } else if (features && features.batchBarcodes === false) {
      localStorage.setItem('kiosk_batch_barcodes_disabled', '1');
    }
    const raw = JSON.parse(localStorage.getItem('kiosk_custom_features') || '{}') || {};
    const nextFeatures = { ...raw };
    // Nur hinzufügen — von uns je Konto gesetzte Freischaltungen (custom_features) bleiben, auch wenn der Haken im Setup
    // fehlt; der Server rechnet genauso (effectiveFeatures additiv, Review 19.10.2026)
    for (const n of PROFILE_FEATURES) {
      if (features && features[n]) nextFeatures[n] = true;
    }
    localStorage.setItem('kiosk_custom_features', JSON.stringify(nextFeatures));
    window.dispatchEvent(new CustomEvent('kiosk:custom-features', { detail: nextFeatures }));
  } catch { /* */ }
  const res = await saveAccountProfile({ replace: true, settings, features, carriers });
  if (!res || !res.profile) throw new Error('Profil konnte nicht gespeichert werden');
  cacheProfile(res.profile);
  const changed = applySettings(res.profile.settings);
  snapshot = collectLocalSettings();
  starteBeobachter();
  benachrichtige(changed);
  return res.profile;
}

/** Abmelden / Kontowechsel: Abgleich anhalten, damit nichts ins falsche Konto geschrieben wird. */
export function resetProfileSync() {
  snapshot = null;
  pausiert = false;
  uebergangGeprueft = false;
  cacheProfile(null);
}
